import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { packageSchema, PackageFormData, Package } from '@/features/packages/types';
import { Loader2 } from 'lucide-react';

interface PackageFormProps {
  initialData?: Package;
  isLoading?: boolean;
  onSubmit: (data: PackageFormData) => Promise<void>;
  onCancel?: () => void;
}

export const PackageForm: React.FC<PackageFormProps> = ({
  initialData,
  isLoading = false,
  onSubmit,
  onCancel,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<PackageFormData>({
    resolver: zodResolver(packageSchema),
    defaultValues: initialData
      ? {
          name: initialData.name,
          description: initialData.description,
          monthly_price: initialData.monthly_price || undefined,
          yearly_price: initialData.yearly_price || undefined,
          package_type: initialData.package_type,
          is_addon: initialData.is_addon,
          is_featured: initialData.is_featured,
          display_order: initialData.display_order,
          color_index: initialData.color_index || undefined,
          features: initialData.features || [],
          marketing_tagline: initialData.marketing_tagline,
          best_for: initialData.best_for,
        }
      : {
          name: '',
          description: null,
          monthly_price: undefined,
          yearly_price: undefined,
          package_type: 'mixed',
          is_addon: false,
          is_featured: false,
          display_order: 0,
          color_index: undefined,
          features: [],
          marketing_tagline: null,
          best_for: null,
        },
  });

  const handleSubmit = async (data: PackageFormData) => {
    try {
      setIsSubmitting(true);
      await onSubmit(data);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        {/* Name */}
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Package Name *</FormLabel>
              <FormControl>
                <Input placeholder="e.g., Premium Sports Bundle" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Description */}
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Describe what's included in this package..."
                  rows={3}
                  {...field}
                  value={field.value || ''}
                />
              </FormControl>
              <FormDescription>Optional description of the package</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Package Type */}
        <FormField
          control={form.control}
          name="package_type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Package Type *</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="standalone">
                    Standalone - Direct channel assignments
                  </SelectItem>
                  <SelectItem value="plan_bundle">
                    Plan Bundle - Combination of multiple plans
                  </SelectItem>
                  <SelectItem value="category_bundle">
                    Category Bundle - Combination of multiple categories
                  </SelectItem>
                  <SelectItem value="mixed">
                    Mixed - Combination of plans, categories & channels
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormDescription>
                Determines how channels are resolved for this package
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          {/* Monthly Price */}
          <FormField
            control={form.control}
            name="monthly_price"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Monthly Price</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    placeholder="0.00"
                    step="0.01"
                    {...field}
                    value={field.value || ''}
                    onChange={(e) =>
                      field.onChange(e.target.value ? parseFloat(e.target.value) : null)
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Yearly Price */}
          <FormField
            control={form.control}
            name="yearly_price"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Yearly Price</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    placeholder="0.00"
                    step="0.01"
                    {...field}
                    value={field.value || ''}
                    onChange={(e) =>
                      field.onChange(e.target.value ? parseFloat(e.target.value) : null)
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Color Index */}
          <FormField
            control={form.control}
            name="color_index"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Color Index (1-30)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    placeholder="1"
                    min="1"
                    max="30"
                    {...field}
                    value={field.value || ''}
                    onChange={(e) =>
                      field.onChange(e.target.value ? parseInt(e.target.value) : null)
                    }
                  />
                </FormControl>
                <FormDescription>Visual identifier for the package</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Display Order */}
          <FormField
            control={form.control}
            name="display_order"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Display Order</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    placeholder="0"
                    {...field}
                    onChange={(e) => field.onChange(parseInt(e.target.value))}
                  />
                </FormControl>
                <FormDescription>Order for displaying packages</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Marketing Tagline */}
        <FormField
          control={form.control}
          name="marketing_tagline"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Marketing Tagline</FormLabel>
              <FormControl>
                <Input
                  placeholder="e.g., 'Perfect for sports fans'"
                  {...field}
                  value={field.value || ''}
                />
              </FormControl>
              <FormDescription>Short promotional text</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Best For */}
        <FormField
          control={form.control}
          name="best_for"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Best For</FormLabel>
              <FormControl>
                <Input
                  placeholder="e.g., 'Families with kids'"
                  {...field}
                  value={field.value || ''}
                />
              </FormControl>
              <FormDescription>Target audience description</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-3 gap-4">
          {/* Is Addon */}
          <FormField
            control={form.control}
            name="is_addon"
            render={({ field }) => (
              <FormItem className="flex items-center space-x-2">
                <FormControl>
                  <input
                    type="checkbox"
                    checked={field.value}
                    onChange={field.onChange}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                </FormControl>
                <FormLabel className="!mt-0">Is Add-on</FormLabel>
              </FormItem>
            )}
          />

          {/* Is Featured */}
          <FormField
            control={form.control}
            name="is_featured"
            render={({ field }) => (
              <FormItem className="flex items-center space-x-2">
                <FormControl>
                  <input
                    type="checkbox"
                    checked={field.value}
                    onChange={field.onChange}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                </FormControl>
                <FormLabel className="!mt-0">Is Featured</FormLabel>
              </FormItem>
            )}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4">
          <Button
            type="submit"
            disabled={isSubmitting || isLoading}
            className="flex items-center gap-2"
          >
            {isSubmitting || isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : initialData ? (
              'Update Package'
            ) : (
              'Create Package'
            )}
          </Button>
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </div>
      </form>
    </Form>
  );
};

export default PackageForm;
